import { Plus } from '@ethsign/icons';

import { Button, Label, Select } from '@ethsign/ui';

import { shortenWalletAddress } from '@ethsign/utils-web';

import { useQuery } from '@tanstack/react-query';

import { concat, every, isEmpty, omit, pick } from 'lodash-es';

import React, { useMemo, useState } from 'react';

import { AddressInfo, Card } from '@/components';
import { AutoCompleteOfSingle } from '@/components/TextField/AutoComplete';
import { NETWORK_TYPE_DISPLAY, NETWORK_TYPE_OPTIONS } from '@/constants';
import { useUserInfo } from '@/hooks';
import { searchContacts } from '@/services';
import { IContact } from '@/types';
import { getNetworkByAddress, WalletType } from '@/utils';

import { formatCurrentUser, getAddressByEns } from './utils';

const SignerSearchSelect = ({
  dataSource,
  signer,
  onChange,
  className,
  searchKey,
  placeholder
}: {
  searchKey: string;
  className?: string;
  dataSource: any[];
  signer: any;
  onChange: (v: Record<string, any>) => void;
  placeholder?: string;
}) => {
  const [options, setOptions] = useState<any[]>([]);
  const renderOptions = useMemo(
    () =>
      options?.map((item) => ({
        label: (
          <div className={'flex items-center justify-between w-full text-sm ml-2'}>
            <div className="text-left">
              <div className="flex flex-row">
                <div className={'max-w-[150px] truncate text-gray-700  font-medium'} title={item.name}>
                  {item.name}
                </div>
                <div className={'flex-1 truncate min-w-1 ml-1 text-right text-gray-500'} title={item.email}>
                  {item.email}
                </div>
              </div>

              <AddressInfo
                className={'text-gray-500 mt-1'}
                address={item.walletAddress}
                formatAddress={(address) => shortenWalletAddress(address, 'shorter')}
                network={item.network as WalletType}
              />
            </div>
          </div>
        ),
        value: item.walletAddress,
        data: item
      })),
    [options]
  );

  const filterOptionsByKey = (keyword: string) => {
    const filterOptions =
      dataSource?.filter((item) => {
        return item[searchKey]?.toLowerCase().includes(keyword?.toLowerCase());
      }) || [];
    setOptions(filterOptions);
  };

  const handleSearch = async (keyword: string) => {
    // 其他输入框都没有值才搜索
    const otherFields = omit(signer, [searchKey, 'signerId']);
    const isAllFieldsEmpty = every(otherFields, isEmpty);
    console.log(signer, otherFields, isAllFieldsEmpty);
    if (!isAllFieldsEmpty) {
      // 清空options
      if (options?.length) {
        setOptions([]);
      }
      return;
    }
    if (searchKey === 'walletAddress') {
      if (keyword && keyword?.endsWith('.eth')) {
        const address = await getAddressByEns(keyword);
        if (address) {
          setOptions([
            {
              walletAddress: address,
              network: WalletType.EVM,
              name: keyword
            }
          ]);
        }
      } else {
        filterOptionsByKey(keyword);
      }
    } else {
      filterOptionsByKey(keyword);
    }
  };

  return (
    <AutoCompleteOfSingle
      value={signer[searchKey]}
      onChange={(v, item) => {
        // 清空walletAddress同时清空network
        const signerData = item?.data
          ? pick(item.data, ['walletAddress', 'email', 'telegram', 'name', 'network'])
          : { [searchKey]: v };
        if (searchKey === 'walletAddress' && !v) {
          signerData.network = '';
        }
        if (searchKey === 'walletAddress' && v) {
          signerData.network = getNetworkByAddress(v);
        }
        onChange(signerData);
      }}
      className={className}
      options={renderOptions?.length ? renderOptions : []}
      placeholder={placeholder}
      onSearch={handleSearch}
    />
  );
};

export const AddSignerCard = ({
  item,
  value: signer = {
    name: '',
    network: '',
    walletAddress: '',
    email: '',
    telegram: ''
  },
  onChange: setSigner
}: {
  item: any;
  value?: IContact;
  onChange: (v: IContact) => void;
}) => {
  const { data: contacts } = useQuery({
    queryKey: ['contacts-search'],
    queryFn: () => searchContacts(),
    select: (res) => res.rows
  });
  const { user } = useUserInfo();
  const isDisableCurrentUser = user?.displayAddress === signer.walletAddress;

  const updateSigner = (v: Partial<IContact>) => {
    setSigner({ ...signer, ...v });
  };

  return (
    <div>
      <Card
        title={item.name}
        subTitle={`Input signer’s information for the ${item.name}.`}
        extra={
          <Button
            variant={'outline'}
            onClick={() => {
              const selfSigner = formatCurrentUser(user!);
              updateSigner(selfSigner);
            }}
          >
            <Plus color="#344054" size={20} />
            <span className="ml-2">Add Myself</span>
          </Button>
        }
      >
        <div className="py-4 px-6 space-y-4">
          <div>
            <Label>Wallet Address</Label>
            <div className="flex mt-1.5">
              <SignerSearchSelect
                className={'rounded-r-none'}
                dataSource={contacts || []}
                searchKey={'walletAddress'}
                signer={signer}
                onChange={updateSigner}
                placeholder={'Enter wallet address'}
              />
              <Select
                allowClear
                placeholder={'Select Network'}
                className={'rounded-l-none border-l-0 flex-[0_0_160px]'}
                options={concat(
                  NETWORK_TYPE_OPTIONS,
                  isDisableCurrentUser
                    ? [{ label: 'EVM Compatible Networks(Particle)', value: WalletType.PARTICLE }]
                    : []
                )}
                value={signer.network}
                onChange={(v) => updateSigner({ network: v })}
                displaySelectValue={(v) => {
                  if (v === WalletType.EVM) {
                    return 'EVM';
                  }
                  return NETWORK_TYPE_DISPLAY[v as WalletType];
                }}
              />
            </div>
          </div>
          <div className={'flex gap-5'}>
            <div className={'flex-1'}>
              <Label>Email</Label>
              <SignerSearchSelect
                dataSource={contacts || []}
                searchKey={'email'}
                signer={signer}
                onChange={updateSigner}
                placeholder={'Enter email'}
                className={'mt-1.5'}
              />
            </div>
            <div className={'flex-1'}>
              <Label>Telegram</Label>
              <SignerSearchSelect
                dataSource={contacts || []}
                searchKey={'telegram'}
                signer={signer}
                onChange={updateSigner}
                placeholder={'@handle'}
                className={'mt-1.5'}
              />
            </div>
          </div>
          <div>
            <Label>Signer’s Name</Label>
            <SignerSearchSelect
              dataSource={contacts || []}
              searchKey={'name'}
              signer={signer}
              onChange={updateSigner}
              placeholder={'Enter name'}
              className={'mt-1.5'}
            />
          </div>
        </div>
      </Card>
    </div>
  );
};

export const InviteSignerForTemplate = ({
  onChange,
  data,
  signers
}: {
  onChange: (v: IContact[]) => void;
  data: IContact[];
  signers: any[];
}) => {
  return (
    <div className={'grid grid-cols-2 gap-6'}>
      {signers?.map((it, i) => {
        return (
          <AddSignerCard
            key={it.id}
            item={it}
            value={data[i]}
            onChange={(v) => {
              const newData = [...data];
              newData[i] = v;
              onChange(newData);
            }}
          />
        );
      })}
    </div>
  );
};
